<template>
  <div ref="tooltipRef" class="font-inter text-sm leading-normal text-popover-foreground min-w-[200px] max-w-[320px] box-border">
    <!-- Simplified API Response Tooltip -->
    <div v-if="isApiResponse" class="flex flex-col gap-2">
      <!-- Source logo and info for API responses -->
      <div class="flex items-center gap-3 mb-2">
        <div class="flex-shrink-0">
          <!-- Complete source logo (no background needed) -->
          <Icon :name="getSourceIcon(sourceName)" class="h-6 w-auto" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-xs text-muted-foreground">{{ fieldName }} information</div>
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <button 
            v-if="processedText && processedText !== sourceText"
            @click="showProcessed = !showProcessed"
            class="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {{ showProcessed ? 'Raw' : 'Formatted' }}
          </button>
        </div>
      </div>
      <ScrollArea showScrollIndicator class="h-40 mt-2 pr-2 text-xs"
        thumbClass="!bg-primary"
        :indicatorGradientFrom="'transparent'"
        :indicatorGradientVia="'background/40'"
        :indicatorGradientTo="'background/80'"
        :indicatorIconColor="'muted-foreground'"
      >
        <div v-if="sourceText" class="text-xs text-muted-foreground leading-normal whitespace-pre-wrap break-words">
          <div v-if="showProcessed && processedText" class="text-xs leading-normal">
            <VueMarkdown :markdown="cleanCitations(processedText)" :custom-attrs="markdownCustomAttrs" />
          </div>
          <div v-else class="text-xs leading-normal">
            <VueMarkdown :markdown="cleanCitations(sourceText)" :custom-attrs="markdownCustomAttrs" />
          </div>
        </div>
      </ScrollArea>
    </div>
    
    <!-- Standard Tooltip for non-API data -->
    <div v-else class="flex flex-col gap-2">
      <div class="flex flex-col gap-1">
        <div class="font-bold text-sm text-popover-foreground">{{ fieldName }}</div>
        <div v-if="fieldDescription" class="text-xs text-muted-foreground italic">{{ fieldDescription }}</div>
      </div>
      <!-- Only show field value if it's different from the description (for cell tooltips) -->
      <div v-if="value && value !== fieldDescription" class="text-sm text-popover-foreground break-words">{{ value }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { onClickOutside, onKeyStroke } from '@vueuse/core'

interface Props {
  params?: any
}

const props = defineProps<Props>()

// Template ref for the tooltip container
const tooltipRef = ref<HTMLElement>()

// Reactive properties for tooltip content
const value = ref('')
const fieldName = ref('')
const fieldDescription = ref('')
const isApiResponse = ref(false)
const sourceName = ref('')
const sourceText = ref('')
const sourceUrls = ref<string[]>([])
const processedText = ref('')
const showProcessed = ref(false)

// Function to clean citations from text
const cleanCitations = (text: string): string => {
  if (!text) return text
  // Remove citation patterns like [1], [2], [1][2], etc.
  return text.replace(/\[\d+\](?:\[\d+\])*/g, '')
}

// Custom attributes for markdown elements based on shadcn-vue typography
const markdownCustomAttrs = {
  h1: {
    class: 'scroll-m-20 text-3xl font-extrabold tracking-tight lg:text-4xl mt-2'
  },
  h2: {
    class: 'scroll-m-20 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0 mt-2'
  },
  h3: {
    class: 'scroll-m-20 text-xl font-semibold tracking-tight mt-2'
  },
  h4: {
    class: 'scroll-m-20 text-lg font-semibold tracking-tight mt-2'
  },
  p: {
    class: 'leading-5 [&:not(:first-child)]:mt-2'
  },
  blockquote: {
    class: 'mt-3 border-l-2 pl-4 italic'
  },
  table: {
    class: 'my-6 w-full overflow-y-auto'
  },
  tr: {
    class: 'm-0 border-t p-0 even:bg-muted'
  },
  th: {
    class: 'border px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right'
  },
  td: {
    class: 'border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right'
  },
  ul: {
    class: 'my-3 ml-4 list-disc [&>li]:mt-1'
  },
  ol: {
    class: 'my-3 ml-4 list-decimal [&>li]:mt-1'
  },
  code: {
    class: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold'
  },
  pre: {
    class: 'mb-4 mt-6 overflow-x-auto rounded-lg border bg-zinc-950 py-4'
  },
  a: {
    class: 'font-medium text-primary underline underline-offset-4',
    target: '_blank',
    rel: 'noopener noreferrer'
  }
}

// Enhanced metadata processing
const processMetadata = () => {
  if (!props.params) return
  
  const cellValue = props.params.value
  const colDef = props.params.colDef
  
  fieldName.value = colDef?.headerName || colDef?.field || 'Field'
  fieldDescription.value = colDef?.headerTooltip || ''
  
  // Check if this is an API response with metadata
  if (typeof cellValue === 'object' && cellValue !== null) {
    const hasApiData = cellValue.sourceName || cellValue.groundingMetadata || cellValue.sourceText
    
    if (hasApiData) {
      isApiResponse.value = true
      
      // Extract basic information
      sourceName.value = cellValue.sourceName || cellValue.source || 'API'
      sourceText.value = cellValue.sourceText || ''
      
      // Extract source URLs from various possible locations
      const urls: string[] = []
      
      // From grounding metadata
      if (cellValue.groundingMetadata?.groundingSupports) {
        cellValue.groundingMetadata.groundingSupports.forEach((support: any) => {
          if (support.groundingChunkWeb?.uri) {
            urls.push(support.groundingChunkWeb.uri)
          }
        })
      }
      
      // From sourceUrls array
      if (Array.isArray(cellValue.sourceUrls)) {
        urls.push(...cellValue.sourceUrls)
      }
      
      // From direct sourceUrl property
      if (cellValue.sourceUrl) {
        urls.push(cellValue.sourceUrl)
      }
      
      sourceUrls.value = [...new Set(urls)] // Remove duplicates
    }
  }
  
  // Set display value
  if (typeof cellValue === 'object' && cellValue !== null) {
    value.value = cellValue.label || cellValue.value || cellValue.displayValue || ''
  } else {
    value.value = cellValue || ''
  }
}

// Helper function for source icons (complete logos with colors and logotypes)
const getSourceIcon = (source: string): string => {
  const lowerSource = source.toLowerCase()
  
  if (lowerSource.includes('perplexity')) return 'logos:perplexity'
  if (lowerSource.includes('google') || lowerSource.includes('gemini')) return 'logos:google-gemini'
  if (lowerSource.includes('openai') || lowerSource.includes('gpt')) return 'logos:openai'
  if (lowerSource.includes('anthropic') || lowerSource.includes('claude')) return 'logos:claude'
  if (lowerSource.includes('gbif')) return 'mdi:leaf'
  if (lowerSource.includes('wiki')) return 'simple-icons:wikipedia'
  if (lowerSource.includes('scholar')) return 'mdi:school'
  if (lowerSource.includes('pubmed') || lowerSource.includes('ncbi')) return 'mdi:dna'
  if (lowerSource.includes('api')) return 'mdi:api'
  
  // Default icon for unknown sources
  return 'mdi:web'
}

onMounted(() => {
  processMetadata()
  
//   // Additional event listeners for hiding tooltip on grid interactions
//   const handleGridScroll = () => hideTooltip()
//   const handleFocusLoss = () => hideTooltip()
  
//   // Listen for scroll events on the grid container
//   document.addEventListener('scroll', handleGridScroll, true)
//   // Listen for focus changes
//   window.addEventListener('blur', handleFocusLoss)
  
//   // Cleanup function
//   onUnmounted(() => {
//     document.removeEventListener('scroll', handleGridScroll, true)
//     window.removeEventListener('blur', handleFocusLoss)
//   })
})

// Helper function to hide tooltip
const hideTooltip = () => {
  if (props.params?.hideTooltipCallback) {
    props.params.hideTooltipCallback()
  }
}

// Set up click outside handler to hide tooltip
// onClickOutside(tooltipRef, hideTooltip)

// Set up escape key handler to hide tooltip
onKeyStroke('Escape', hideTooltip)
</script>

<style scoped>
/* Keep minimal override for text color consistency in tooltip context */
.markdown-content :deep(p) {
  color: hsl(var(--muted-foreground));
}

.markdown-content :deep(strong) {
  font-weight: 600;
  color: hsl(var(--popover-foreground));
}

.markdown-content :deep(em) {
  font-style: italic;
}
</style> 